import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AuthService,
    BrowserPlatformProvider,
    FusionAuthSPAAuthProvider,
    AppState,
} from '@landr/auth/services';
import { Languages } from '@landr/core.models';
import { CONFIG } from 'utils/config';
import { getTargetUrl } from 'helpers';
import { analytics, i18n } from 'utils';
import { log } from 'utils/log';
import { getEnvSuffix } from '@landr/core';
import { EventIds, UTMCookieSettings } from '../../constants';
import { getAuthToken } from './getAuthToken';

const environment = CONFIG.VITE_APP_ENV;

const platformProvider = new BrowserPlatformProvider({
    environment,
    analytics,
    cookieSettings: UTMCookieSettings,
});
const authProvider = new FusionAuthSPAAuthProvider({
    domain: CONFIG.VITE_FUSION_AUTH_DOMAIN,
    clientId: CONFIG.VITE_FUSION_AUTH_CLIENT_ID,
    platformProvider,
    environment,
    log: log.getInstance('FusionAuthSPAAuthProvider'),
});

export const authService = new AuthService({
    authProvider,
    platformProvider,
    environment,
    homepageForAnalyticsFallback: 'Samples',
    log: log.getInstance('AuthService'),
});

const getAuthorizationHeader = (token?: string): object | undefined => {
    return token ? { authorization: `Bearer ${token}` } : undefined;
};

export type User = {
    city?: string;
    country: string;
    email: string;
    firstname: string;
    displayName?: string;
    homePage?: string;
    isLoggedOnBehalf?: boolean;
    lastname?: string;
    libraryId?: string;
    preferredCulture?: string;
    profilePicture72Url?: string;
    state?: string;
    userId: string;
};

const loadUser = async (
    isAuthenticated: boolean,
    token: string | undefined,
): Promise<User | undefined> => {
    try {
        if (!isAuthenticated) {
            return undefined;
        }

        const authorization = getAuthorizationHeader(token);
        const response = await fetch(`${CONFIG.VITE_MS_USER}/me/profile`, {
            headers: { ...authorization },
        });
        const user: User = await response.json();

        return {
            ...user,
        };
    } catch (e) {
        log.error(
            'Failed to handle loadUser',
            EventIds.AuthFetchProfileError,
            e,
        );

        return undefined;
    }
};

/**
 * Exporting for tests only
 * Do not use directly.
 */
export const useAuthenticationEffects = (
    isDAWPlugin: boolean,
    isDesktopApp: boolean,
    sendAuthToken: (token: string) => void,
    sendLogout: () => void,
): {
    appState?: AppState;
    isAuthenticated: boolean;
    isGuestUser: boolean;
    isLoading: boolean;
    isSignup: boolean;
    user?: User;
    login: (appState?: AppState) => void;
    logout: () => void;
    signup: (appState?: AppState) => void;
    continueOrRedirectToProfileSurvey: () => void;
} => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isGuestUser, setIsGuestUser] = useState(false);
    const [user, setUser] = useState<User | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const [appState, setAppState] = useState<AppState | undefined>(undefined);

    useEffect(() => {
        let isCancelled = false;

        const initAuthService = async (): Promise<void> => {
            const isAuthenticatedLocal =
                await authService.checkOrWaitForOngoingAuthentication();

            // Redirect to login flow "forced"
            if (!isAuthenticatedLocal && (isDAWPlugin || isDesktopApp)) {
                authService.login({
                    appState: {
                        targetUrl: getTargetUrl(),
                    },
                    locale: i18n.locale as Languages,
                    forceLoginPrompt: true,
                });
            }

            const token = await authService.getAccessToken();

            if (isDAWPlugin) {
                sendAuthToken(token ?? '');
            }

            const gettingUser = isAuthenticatedLocal
                ? loadUser(isAuthenticatedLocal, token ?? '')
                : Promise.resolve(undefined);

            if (isAuthenticatedLocal) {
                const localAppState = authService.getAppState();

                if (localAppState && localAppState.targetUrl) {
                    navigate(localAppState.targetUrl, { replace: true });
                }

                if (localAppState) {
                    setAppState(localAppState);
                }
            }

            if (!isCancelled) {
                const user = await gettingUser;

                setUser(user);

                setIsAuthenticated(isAuthenticatedLocal);
                setIsGuestUser(!isAuthenticatedLocal);

                setIsLoading(false);
            }
        };

        initAuthService();

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        async function updateAuthTokenForDawPlugin() {
            if (isDAWPlugin) {
                const token = await getAuthToken();

                sendAuthToken(token ? token : '');
            }
        }

        updateAuthTokenForDawPlugin();
        // Only run on location change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const login = () => {
        authService &&
            authService.login({
                appState: {
                    targetUrl: getTargetUrl(),
                },
                locale: i18n.locale as Languages,
            });
    };

    const signup = () => {
        authService &&
            authService.login({
                isSignup: true,
                appState: {
                    targetUrl: getTargetUrl(),
                },
                locale: i18n.locale as Languages,
            });
    };

    const logout = useCallback(() => {
        if (isDAWPlugin) {
            sendLogout();
        }

        return authService?.logout();
    }, [isDAWPlugin, sendLogout]);

    const isSignup = !!authService.getIsSignup();

    const continueOrRedirectToProfileSurvey = () => {
        if (isSignup && !isDAWPlugin) {
            const profileSurveyPathname = `/profile-survey?isSignup=true&redirectUrl=${window.location.href}`;

            // Don't use getWebappLink here because it will redirect to local environment
            window.location.assign(
                `https://app${getEnvSuffix(
                    environment,
                )}.landr.com${profileSurveyPathname}`,
            );
        }
    };

    return {
        appState,
        isAuthenticated,
        isGuestUser,
        isLoading,
        isSignup,
        user,
        login,
        logout,
        signup,
        continueOrRedirectToProfileSurvey,
    };
};

export default useAuthenticationEffects;
